import { HotelRatingSquare } from '@lt/components';
import { useTranslation } from '@lt/localization';
import React from 'react';
import styled from 'styled-components';
import { TEST_ID_HOTEL_RATING_CONTAINER } from './constants';
import { getRatingStyle } from './utils/ratingUtils';

export interface CategoryListProps {
  score: number;
  categoryName: string;
}

export interface HotelRatingProps {
  categoryList: CategoryListProps[];
  score: number;
  text: string;
  count: number;
  isMobile?: boolean;
  hasRating: boolean;
}

export const HotelRating = ({
  score,
  text,
  categoryList,
  count,
  isMobile = false,
  hasRating,
}: HotelRatingProps) => {
  const { t } = useTranslation();
  if (!hasRating) return null;
  return (
    <Container data-testid={TEST_ID_HOTEL_RATING_CONTAINER}>
      <StyledTitleContainer>
        <HotelRatingSquare value={score} count={score} />
        <StyledHeader>
          <StyledTitle isMobile={isMobile}>{text}</StyledTitle>
          <StyledSubHeader>
            {t('hotel.hotelRating.title', { count })}
          </StyledSubHeader>
        </StyledHeader>
      </StyledTitleContainer>
      <CategoryListContainer isMobile={isMobile}>
        {categoryList.map((category) => (
          <StyledCategory key={category.categoryName} isMobile={isMobile}>
            <CategoryContainer>
              <StyledCategoryName>{category.categoryName}</StyledCategoryName>
              <StyledScore $color={getRatingStyle(category.score)}>
                {category.score}
              </StyledScore>
            </CategoryContainer>
            <StyledProgressBar>
              <StyledRatingProgressBar
                $progress={category.score * 10}
                $color={getRatingStyle(category.score)}
              />
            </StyledProgressBar>
          </StyledCategory>
        ))}
      </CategoryListContainer>
    </Container>
  );
};

export default HotelRating;

const Container = styled.div`
  background: #fff;
  border-radius: 6px;
  padding: 16px;
  heigth: fit-content;
`;

const StyledTitleContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 16px;
`;

const StyledTitle = styled.div<{ isMobile: boolean }>`
  font-weight: 700;
  font-size: ${({ isMobile }) => (isMobile ? 20 : 22)}px;
`;

const CategoryListContainer = styled.div<{ isMobile: boolean }>`
  ${({ isMobile }) =>
    isMobile &&
    `
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    `}
`;

const CategoryContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 5px;
`;

const StyledProgressBar = styled.div`
  width: 100%;
  height: 4px;
  background: #dfdddc;
  border-radius: 13px;
  position: relative;
`;

const StyledRatingProgressBar = styled.div<{
  $progress: number;
  $color: string;
}>`
  position: absolute;
  height: 4px;
  width: ${({ $progress }) => $progress}%;
  background: ${({ $color }) => $color};
`;

const StyledCategoryName = styled.div`
  font-size: 14px;
  color: #17181a;
`;

const StyledCategory = styled.div<{ isMobile: boolean }>`
  margin-bottom: 16px;
  ${({ isMobile }) =>
    isMobile &&
    `
    display: flex;
    flex-direction: column;
    align-self: end;
    flex-basis: 45%;
  `}
`;

const StyledScore = styled.div<{ $color: string }>`
  color: ${({ $color }) => $color};
`;

const StyledSubHeader = styled.div`
  color: #4d4d4d;
  font-size: 14px;
`;

const StyledHeader = styled.div`
  margin-left: 12px;
`;
